import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FileSpreadsheet, FileText, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { appliquerLargeurs, cles, colonnes, colonneTriable, donneesExport, largeurAuto, largeursActuelles, trierTable } from "@/lib/tableTri";
import { errMsg, telechargerExcel, telechargerPdf } from "@/lib/api";

const cleStockage = (id) => `rb_table_${id}`;
const lire = (id) => {
  try { return JSON.parse(localStorage.getItem(cleStockage(id))) || {}; } catch { return {}; }
};

const useEtat = (id) => {
  const [etat, setEtat] = useState(() => ({ w: {}, tri: null, ...lire(id) }));
  useEffect(() => {
    if (Object.keys(etat.w).length || etat.tri) localStorage.setItem(cleStockage(id), JSON.stringify(etat));
    else localStorage.removeItem(cleStockage(id));
  }, [id, etat]);
  return [etat, setEtat];
};

const marquerEntetes = (t, ks, tri) => colonnes(t).forEach((th, i) => {
  if (ks[i].startsWith("#") || !colonneTriable(t, i)) delete th.dataset.stTriable; else th.dataset.stTriable = "";
  if (tri?.k === ks[i]) th.dataset.stTri = tri.dir; else delete th.dataset.stTri;
});

const calculerBords = (t, ks) => {
  const r0 = t.getBoundingClientRect();
  const h = t.tHead?.offsetHeight || 0;
  const y = t.tHead ? Math.max(0, Math.round(t.tHead.getBoundingClientRect().top - r0.top)) : 0;
  return colonnes(t).map((th, i) => ({ k: ks[i], i, x: Math.round(th.getBoundingClientRect().right - r0.left), y, h, vis: th.offsetWidth > 0 })).filter((b) => b.vis);
};

const PETIT = "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50";

const Barre = ({ id, perso, onReset, onExcel, onPdf, busy }) => (
  <div className="flex justify-end gap-1 py-1">
    {perso && (
      <button type="button" onClick={onReset} data-testid={`${id}-reinit-colonnes`} className={PETIT}>
        <RotateCcw className="h-3 w-3" />Réinitialiser les colonnes
      </button>
    )}
    <button type="button" onClick={onExcel} disabled={busy} title="Télécharger ce tableau en Excel, dans l'ordre affiché" data-testid={`${id}-export-excel`} className={PETIT}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 text-emerald-700" />}Excel
    </button>
    <button type="button" onClick={onPdf} disabled={busy} title="Télécharger ce tableau en PDF pour impression, dans l'ordre affiché" data-testid={`${id}-export-pdf`} className={PETIT}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3 text-emerald-700" />}PDF
    </button>
  </div>
);

const Poignee = ({ id, b, onDrag, onFit }) => (
  <span onMouseDown={(e) => onDrag(e, b.k)} onDoubleClick={() => onFit(b.k)} title="Glisser pour élargir · double-clic pour ajuster"
    className="group/p absolute top-0 z-20 hidden w-3 -translate-x-1/2 cursor-col-resize justify-center sm:flex"
    style={{ left: b.x, top: b.y, height: b.h }} data-testid={`${id}-poignee-${b.i}`}>
    <span className="h-full w-px bg-slate-200/70 transition-colors group-hover/p:w-0.5 group-hover/p:bg-sky-500" />
  </span>
);

export const SmartTable = ({ id, exporter, children, ...props }) => {
  const ref = useRef(null);
  const [etat, setEtat] = useEtat(id);
  const [version, setVersion] = useState(0);
  const [bords, setBords] = useState([]);
  const etatRef = useRef(etat);
  etatRef.current = etat;
  useEffect(() => { if (ref.current) ref.current.stExport = exporter; });

  const majBords = useCallback(() => {
    const t = ref.current;
    if (!t) return;
    const b = calculerBords(t, cles(t));
    setBords((old) => (JSON.stringify(old) === JSON.stringify(b) ? old : b));
  }, []);

  const synchro = useCallback(() => {
    const t = ref.current;
    if (!t) return;
    const { w, tri } = etatRef.current;
    const ks = cles(t);
    marquerEntetes(t, ks, tri);
    appliquerLargeurs(t, w);
    const i = tri ? ks.indexOf(tri.k) : -1;
    if (i >= 0) trierTable(t, i, tri.dir === "asc" ? 1 : -1);
    majBords();
  }, [majBords]);

  useLayoutEffect(() => { synchro(); }, [etat, version, synchro]);
  useEffect(() => {
    const t = ref.current;
    if (!t) return undefined;
    let raf = 0;
    const planifier = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(synchro); };
    const mo = new MutationObserver(planifier);
    mo.observe(t, { childList: true, subtree: true });
    const ro = new ResizeObserver(planifier);
    ro.observe(t);
    const defiler = () => requestAnimationFrame(majBords);
    window.addEventListener("scroll", defiler, true);
    return () => { mo.disconnect(); ro.disconnect(); cancelAnimationFrame(raf); window.removeEventListener("scroll", defiler, true); };
  }, [version, synchro, majBords]);

  const clic = (e) => {
    const th = e.target.closest("th");
    if (!th?.hasAttribute("data-st-triable") || e.target.closest("button,input,select,a,[role=checkbox]")) return;
    const t = ref.current;
    const i = colonnes(t).indexOf(th);
    if (i < 0) return;
    const k = cles(t)[i];
    const tri = etat.tri;
    if (tri?.k === k && tri.dir === "desc") {
      setEtat((s) => ({ ...s, tri: null }));
      setVersion((v) => v + 1);
      return;
    }
    setEtat((s) => ({ ...s, tri: { k, dir: tri?.k === k ? "desc" : "asc" } }));
  };

  const glisser = (e, k) => {
    e.preventDefault();
    const base = largeursActuelles(ref.current);
    const x0 = e.clientX;
    const bouger = (ev) => setEtat((s) => ({ ...s, w: { ...base, [k]: Math.max(40, Math.round(base[k] + ev.clientX - x0)) } }));
    const fin = () => {
      window.removeEventListener("mousemove", bouger);
      window.removeEventListener("mouseup", fin);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", bouger);
    window.addEventListener("mouseup", fin);
  };

  const ajuster = (k) => {
    const t = ref.current;
    const base = largeursActuelles(t);
    const px = largeurAuto(t, cles(t).indexOf(k));
    setEtat((s) => ({ ...s, w: { ...base, [k]: px } }));
  };

  const reinit = () => { setEtat({ w: {}, tri: null }); setVersion((v) => v + 1); };
  const perso = Object.keys(etat.w).length > 0 || !!etat.tri;
  const [busy, setBusy] = useState(false);
  const excel = async () => {
    setBusy(true);
    try { await telechargerExcel(props["data-export-titre"] || id, donneesExport(ref.current)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pdf = async () => {
    setBusy(true);
    try { await telechargerPdf(props["data-export-titre"] || id, donneesExport(ref.current)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Barre id={id} perso={perso} onReset={reinit} onExcel={excel} onPdf={pdf} busy={busy} />
      <div className="relative">
        <table key={version} ref={ref} onClick={clic} {...props}>{children}</table>
        {bords.map((b) => <Poignee key={b.k} id={id} b={b} onDrag={glisser} onFit={ajuster} />)}
      </div>
    </>
  );
};
